import React, {
  useState,
  useMemo,
  useCallback,
  useRef,
  useEffect,
} from "react";
import {
  Box,
  Typography,
  TextField,
  InputAdornment,
  Avatar,
  Button,
  Chip,
  CircularProgress,
  useTheme,
  IconButton,
  Menu,
  MenuItem,
  ListItemText,
  Tooltip,
} from "@mui/material";
import SettingsOutlinedIcon from "@mui/icons-material/SettingsOutlined";
import FeedbackOutlinedIcon from "@mui/icons-material/FeedbackOutlined";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import { alpha } from "@mui/material";
import {
  DataGridPro,
  GridColDef,
  GridOverlay,
  GridToolbarColumnsButton,
  GridToolbarContainer,
  GridToolbarDensitySelector,
} from "@mui/x-data-grid-pro";
import { SearchOutlined, MoreVert, Lock } from "@mui/icons-material";
import { useParams, useNavigate } from "react-router-dom";
import { useAppDispatch } from "@/app/hooks";
import { setSelectedItem } from "@/features/bughurd/slice";
import { useTranslation } from "react-i18next";
import {
  useGetListOfBugsQuery,
  useUpdateBugDetailMutation,
  useDeleteBugMutation,
  useGetWidgetByProjectIdQuery,
} from "@/features/bughurd/api";
import { colorPalette, typeToIconMapping } from "../utils";
import { toast } from "react-toastify";
import DeleteConfirmModal from "@/Components/Common/DeleteConfirmModal";
import Pagination from "@/Components/Common/TablePro/Pagination";
import { formatDistanceToNow } from "date-fns";
import TaskUpdateDrawer from "../bugherdComponents/TaskUpdateDrawer";
import { useStyles } from "@/Components/Common/styles";
import { useCustomLocaleText } from "@/Components/Common/useCustomLocaleText";
import useDynamicHeight from "@/Components/scrollable/useDynamicHeight";

interface FeedbackTabProps {
  projectDetails?: any;
  isCurrentUserClient?: boolean;
}

const FeedbackTab: React.FC<FeedbackTabProps> = ({
  projectDetails,
  isCurrentUserClient,
}) => {
  const { t } = useTranslation(["bugherd"]);
  const { id: projectId } = useParams<{ id: string }>();
  const theme = useTheme();
  const classes = useStyles();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const { data: widgetData, isLoading: isWidgetLoading } =
    useGetWidgetByProjectIdQuery({ projectId }, { skip: !projectId });
  const hasWidgetConfig = !!widgetData?.data?.decryptedData?._id;

  const [searchValue, setSearchValue] = useState("");
  const [paginationModel, setPaginationModel] = useState<{
    pageSize: number;
    page: number;
  }>({
    pageSize: 10,
    page: 1,
  });
  const [selectedTaskId, setSelectedTaskId] = useState<string | null>(null);
  const [isTaskModalOpen, setIsTaskModalOpen] = useState(false);

  // Menu state
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [menuRowId, setMenuRowId] = useState<string | null>(null);
  const [menuRowBugFor, setMenuRowBugFor] = useState<string | null>(null);
  const openMenu = Boolean(anchorEl);

  // Delete modal state
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deleteItemId, setDeleteItemId] = useState<string | null>(null);

  // Mutations
  const [updateBug, { isLoading: isUpdating }] = useUpdateBugDetailMutation();
  const [deleteBug, { isLoading: isDeleting }] = useDeleteBugMutation();

  const handleMenuClick = (
    event: React.MouseEvent<HTMLElement>,
    rowId: string,
    bugFor: string,
  ) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
    setMenuRowId(rowId);
    setMenuRowBugFor(bugFor);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleMenuExited = () => {
    setMenuRowId(null);
    setMenuRowBugFor(null);
  };

  const handleViewDetails = () => {
    if (menuRowId) {
      setSelectedTaskId(menuRowId);
      setIsTaskModalOpen(true);
    }
    handleMenuClose();
  };

  const handleMoveToBoard = async () => {
    if (!menuRowId) return;

    try {
      await updateBug({
        bugId: menuRowId,
        bugFor: "BOARD",
      }).unwrap();

      toast.success(t("Feedback moved to board successfully"));
      refetch();
    } catch (error) {
      console.error("Error moving to board:", error);
      toast.error(t("Failed to move feedback to board"));
    }
    handleMenuClose();
  };

  const handleDeleteClick = () => {
    if (menuRowId) {
      setDeleteItemId(menuRowId);
      setDeleteModalOpen(true);
    }
    handleMenuClose();
  };

  const handleDeleteConfirm = async () => {
    if (!deleteItemId) return;

    try {
      await deleteBug(deleteItemId).unwrap();
      toast.success(t("Feedback deleted successfully"));
      setDeleteModalOpen(false);
      setDeleteItemId(null);
      refetch();
    } catch (error) {
      console.error("Error deleting feedback:", error);
      toast.error(t("Failed to delete feedback"));
    }
  };

  const handleDeleteCancel = () => {
    setDeleteModalOpen(false);
    setDeleteItemId(null);
  };

  // Reset pagination when search changes
  useEffect(() => {
    setPaginationModel((prev) => ({ ...prev, page: 1 }));
  }, [searchValue]);

  // Fetch feedback items (public comments/feedback from extension)

  const {
    data: feedbackData,
    isLoading,
    isFetching,
    refetch,
  } = useGetListOfBugsQuery(
    {
      id: projectId,
      page: paginationModel.page,
      limit: paginationModel.pageSize,
      search: searchValue || undefined,
      // bugFor: "FEEDBACK",
      isFeedback: true,
      myTask: isCurrentUserClient,
    },
    { skip: !projectId },
  );

  const feedbackItems = useMemo(() => {
    const items =
      feedbackData?.data?.decryptedData?.items ||
      feedbackData?.data?.items ||
      [];
    return items;
  }, [feedbackData]);

  const totalCount =
    feedbackData?.data?.decryptedData?.totalRecords ||
    feedbackData?.data?.totalRecords ||
    0;

  const handleRowClick = useCallback((params: any) => {
    setSelectedTaskId(params.row._id);
    setIsTaskModalOpen(true);
  }, []);

  const handleCloseTaskModal = useCallback(() => {
    setIsTaskModalOpen(false);
    setSelectedTaskId(null);
  }, []);

  const getInitials = (fullName: string) => {
    if (!fullName) return "";
    const nameParts = fullName?.split(" ");
    if (nameParts?.length === 1) {
      return nameParts[0].substring(0, 2).toUpperCase();
    }
    const initials = nameParts
      .map((part) => part.charAt(0).toUpperCase())
      .join("");
    return initials?.length > 2 ? initials?.substring(0, 2) : initials;
  };

  const priorityConfig: Record<string, { color: string; bgColor: string }> = {
    CRITICAL: { color: "#D32F2F", bgColor: "#FFEBEE" },
    HIGH: { color: "#F57C00", bgColor: "#FFF3E0" },
    MEDIUM: { color: "#FBC02D", bgColor: "#FFFDE7" },
    LOW: { color: "#388E3C", bgColor: "#E8F5E9" },
  };

  const columns: GridColDef[] = useMemo(
    () => [
      {
        field: "bugKey",
        headerName: t("Key"),
        headerClassName: classes.customTableHeader,
        flex: 1,
        renderCell: (params) => {
          const typeConfig =
            typeToIconMapping[params.row.type] || typeToIconMapping.SUGGESTIONS;
          const isMovedToBoard = params.row.bugFor === "BOARD";
          return (
            <Box
              display="flex"
              alignItems="center"
              gap={1}
              className={classes.tableCellData}
              onClick={() => handleRowClick(params)}
              sx={{
                cursor: "pointer",
                "&:hover .bugKey-text": {
                  color: theme.palette.primary.main,
                  textDecoration: "underline",
                },
              }}
            >
              <Box
                sx={{
                  width: 20,
                  height: 20,
                  borderRadius: "4px",
                  backgroundColor: typeConfig?.bgcolor || "#F6C445",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                {typeConfig?.icon}
              </Box>
              <Typography
                variant="body2"
                fontWeight={500}
                className="bugKey-text"
              >
                {params.value}
              </Typography>
              {isMovedToBoard && (
                <Tooltip title={t("Moved to Board")} arrow placement="top">
                  <Lock
                    sx={{
                      fontSize: 16,
                      color: theme.palette.warning.main,
                      ml: 0.5,
                    }}
                  />
                </Tooltip>
              )}
            </Box>
          );
        },
      },
      {
        field: "summary",
        headerName: t("Summary"),
        headerClassName: classes.customTableHeader,
        flex: 1,
        renderCell: (params) => (
          <Typography
            className={classes.tableCellData}
            sx={{
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
            title={params.value}
          >
            {params.value}
          </Typography>
        ),
      },
      {
        field: "createdBy",
        headerName: t("Author"),
        headerClassName: classes.customTableHeader,
        flex: 1,
        renderCell: (params) => {
          const authorName = params.row.createdBy?.fullName || t("Anonymous");
          const rowIndex = params.api.getAllRowIds().indexOf(params.id);
          return (
            <Box
              display="flex"
              alignItems="center"
              gap={1}
              className={classes.tableCellData}
            >
              <Avatar
                sx={{
                  width: 25,
                  height: 25,
                  fontSize: "12px",
                  fontWeight: 500,
                  backgroundColor: colorPalette[rowIndex % colorPalette.length],
                }}
              >
                {getInitials(authorName)}
              </Avatar>
              <Typography variant="body2" fontWeight={500}>
                {authorName}
              </Typography>
            </Box>
          );
        },
      },
      {
        field: "bugStatus",
        headerName: t("Status"),
        headerClassName: classes.customTableHeader,
        flex: 1,
        renderCell: (params) => {
          const status = params.row.bugStatus;
          return (
            <Box className={classes.tableCellData}>
              <Chip
                label={status?.status || t("Open")}
                size="small"
                sx={{
                  backgroundColor: theme.palette.action.selected,
                  color: "text.primary",
                  fontWeight: 500,
                  fontSize: "11px",
                  height: "24px",
                  "& .MuiChip-label": {
                    px: 1,
                  },
                }}
              />
            </Box>
          );
        },
      },
      {
        field: "severity",
        headerName: t("Priority"),
        headerClassName: classes.customTableHeader,
        flex: 1,
        renderCell: (params) => {
          return (
            <Box className={classes.tableCellData}>
              {params.value ? (
                <Chip
                  label={params.value}
                  size="small"
                  sx={{
                    backgroundColor: theme.palette.action.selected,
                    color: "text.primary",
                    fontWeight: 500,
                    fontSize: "11px",
                    height: "24px",
                    "& .MuiChip-label": {
                      px: 1,
                    },
                  }}
                />
              ) : (
                <Typography variant="body2" color="textSecondary">
                  -
                </Typography>
              )}
            </Box>
          );
        },
      },
      {
        field: "pageUrl",
        headerName: t("Source URL"),
        headerClassName: classes.customTableHeader,
        flex: 1,
        renderCell: (params) => {
          const url = params.row.pageUrl || params.row.url || "-";
          return (
            <Typography
              className={classes.tableCellData}
              variant="body2"
              color="primary"
              sx={{
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                cursor: url !== "-" ? "pointer" : "default",
              }}
              title={url}
              onClick={(e) => {
                if (url !== "-") {
                  e.stopPropagation();
                  window.open(url, "_blank");
                }
              }}
            >
              {url}
            </Typography>
          );
        },
      },
      {
        field: "createdAt",
        headerName: t("Created At"),
        headerClassName: classes.customTableHeader,
        flex: 1,
        renderCell: (params) => {
          const date = params.value ? new Date(params.value) : null;
          return (
            <Typography
              className={classes.tableCellData}
              variant="body2"
              color="textSecondary"
            >
              {date ? formatDistanceToNow(date, { addSuffix: true }) : "-"}
            </Typography>
          );
        },
      },
      {
        field: "actions",
        headerName: "",
        headerClassName: classes.customTableHeader,
        width: 60,
        sortable: false,
        filterable: false,
        disableColumnMenu: true,
        renderCell: (params) => (
          <Box className={classes.tableCellData}>
            <IconButton
              size="small"
              onClick={(e) =>
                handleMenuClick(e, params.row._id, params.row.bugFor)
              }
              sx={{
                "&:hover": {
                  backgroundColor: theme.palette.action.hover,
                },
              }}
            >
              <MoreVert fontSize="small" />
            </IconButton>
          </Box>
        ),
      },
    ],
    [t, classes, theme, priorityConfig],
  );

  const GridCustomToolbar = (props: any) => {
    const searchInputRef = useRef<HTMLInputElement>(null);

    const handleClick = () => {
      if (searchInputRef.current) {
        searchInputRef.current.focus();
      }
    };

    useEffect(() => {
      if (searchInputRef.current) {
        searchInputRef.current.focus();
      }
    }, [props.searchValue]);

    return (
      <GridToolbarContainer>
        <GridToolbarColumnsButton />
        <GridToolbarDensitySelector />
        <Box sx={{ flexGrow: 1 }} />

        <TextField
          value={props.value}
          onChange={props.onChange}
          placeholder={t("Search feedback...")}
          inputRef={searchInputRef}
          onClick={handleClick}
          InputProps={{
            disableUnderline: true,
            startAdornment: (
              <InputAdornment position="start">
                <SearchOutlined />
              </InputAdornment>
            ),
          }}
          sx={{
            fieldset: { border: "none" },
          }}
        />
      </GridToolbarContainer>
    );
  };

  const requestSearch = useCallback(
    (val: string) => {
      setSearchValue(val);
    },
    [setSearchValue],
  );

  const localeText = useCustomLocaleText();
  const dynamicHeight = `calc(${useDynamicHeight()} - 40px)`;

  if (isWidgetLoading) {
    return (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          minHeight: 300,
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (!hasWidgetConfig) {
    return (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          minHeight: 450,
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            maxWidth: 420,
            textAlign: "center",
            p: 5,
            borderRadius: "16px",
            border: `1.5px dashed ${alpha(theme.palette.primary.main, 0.3)}`,
            bgcolor: alpha(theme.palette.primary.main, 0.02),
          }}
        >
          <Box
            sx={{
              width: 72,
              height: 72,
              borderRadius: "50%",
              bgcolor: alpha(theme.palette.primary.main, 0.1),
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              mb: 2.5,
            }}
          >
            <FeedbackOutlinedIcon
              sx={{ fontSize: 34, color: "primary.main" }}
            />
          </Box>
          <Typography
            variant="h6"
            fontWeight={700}
            sx={{ mb: 0.75, color: "text.primary" }}
          >
            {t("No Widget Configured")}
          </Typography>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ mb: 3, lineHeight: 1.6 }}
          >
            {t("Set up your feedback widget to start collecting user feedback. Customize colors, questions, and more.")}
          </Typography>
          <Button
            variant="contained"
            size="large"
            endIcon={<ArrowForwardIcon />}
            onClick={() => {
              dispatch(setSelectedItem("Feedback Config"));
              navigate(`/defect-tracker/projects/${projectId}/feedback-config`);
            }}
            sx={{
              textTransform: "none",
              fontWeight: 700,
              borderRadius: "10px",
              px: 4,
              py: 1.25,
              fontSize: 14,
              boxShadow: `0 4px 14px ${alpha(theme.palette.primary.main, 0.3)}`,
              "&:hover": {
                boxShadow: `0 6px 20px ${alpha(theme.palette.primary.main, 0.4)}`,
              },
            }}
          >
            {t("Configure Widget")}
          </Button>
        </Box>
      </Box>
    );
  }

  return (
    <>
      <Box minHeight={"100px"}>
        <DataGridPro
          style={{
            height: dynamicHeight,
          }}
          getRowId={(row) => row?._id}
          columns={columns}
          rows={feedbackItems}
          disableColumnMenu
          pagination={true}
          loading={isLoading || isFetching}
          rowSelection={false}
          disableMultipleColumnsSorting
          localeText={localeText}
          hideFooter={false}
          sx={{
            border: "1px solid",
            borderColor: "divider",
            "& .MuiDataGrid-toolbarContainer": {
              borderBottom: "1px solid",
              borderColor: "divider",
            },
          }}
          slots={{
            toolbar: () => (
              <GridCustomToolbar
                value={searchValue}
                onChange={(e: any) => requestSearch(e.target.value)}
              />
            ),
            pagination: () => (
              <Pagination
                total={totalCount}
                paginationModel={paginationModel}
                setPaginationModel={setPaginationModel}
              />
            ),
            noRowsOverlay: () => (
              <GridOverlay>
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: 1,
                  }}
                >
                  <Typography variant="body1" color="textSecondary">
                    {t("No feedback items found")}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    {t("Feedback from users will appear here")}
                  </Typography>
                </Box>
              </GridOverlay>
            ),
            loadingOverlay: () => (
              <GridOverlay>
                <CircularProgress />
              </GridOverlay>
            ),
          }}
        />
      </Box>

      {/* Task Update Drawer */}
      {selectedTaskId && isTaskModalOpen && (
        <TaskUpdateDrawer
          open={isTaskModalOpen}
          onClose={handleCloseTaskModal}
          setBugId={() => {}}
          setDrawerOpen={() => {}}
          bugId={selectedTaskId}
          users={[]}
          createdBy=""
          setCreatedBy={() => {}}
          refreshColumn={() => {
            refetch();
          }}
          isCurrentUserClient={isCurrentUserClient}
        />
      )}

      {/* Actions Menu */}
      <Menu
        anchorEl={anchorEl}
        open={openMenu}
        onClose={handleMenuClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
        slotProps={{
          transition: {
            onExited: handleMenuExited,
          },
        }}
      >
        <MenuItem onClick={handleViewDetails}>
          <ListItemText>{t("View Details")}</ListItemText>
        </MenuItem>
        {!isCurrentUserClient && menuRowBugFor !== "BOARD" && (
          <MenuItem onClick={handleMoveToBoard} disabled={isUpdating}>
            <ListItemText>
              {isUpdating ? t("Moving...") : t("Move to Board")}
            </ListItemText>
          </MenuItem>
        )}
        {menuRowBugFor !== "BOARD" && (
          <MenuItem onClick={handleDeleteClick}>
            <ListItemText>{t("Delete")}</ListItemText>
          </MenuItem>
        )}
      </Menu>

      {/* Delete Confirmation Modal */}
      <DeleteConfirmModal
        open={deleteModalOpen}
        onClose={handleDeleteCancel}
        onConfirm={handleDeleteConfirm}
        loading={isDeleting}
        title={t("Delete Feedback")}
        confirmMessage={t(
          "Are you sure you want to delete this feedback? This action cannot be undone.",
        )}
        okText={t("Delete")}
      />
    </>
  );
};

export default FeedbackTab;
